import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, RotateCcw, Upload, XCircle } from "lucide-react";
import { ApiError } from "../lib/api";
import { Combobox } from "../components/Combobox";
import { useEventTypes } from "../lib/customers";
import {
  useLeadsPreview,
  useCommitLeadsImport,
  EMPTY_LEADS_MAPPING,
  type LeadsMapping,
  type LeadRowStatus,
  type CommitLeadsResponse,
} from "../lib/leads-import";
import {
  useSalesPreview,
  useCommitSalesImport,
  EMPTY_SALES_MAPPING,
  type SalesMapping,
  type SalesRowStatus,
  type CommitSalesResponse,
} from "../lib/sales-import";
import { useImportHistory, useUndoImport } from "../lib/import-history";

function useProjectId(): number {
  return useOutletContext<{ projectId: number }>().projectId;
}

const inputCls =
  "w-full rounded-md border border-border-primary bg-bg-secondary px-3 py-2 text-sm text-text-primary focus:border-accent focus:outline-none";

function Box({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-3 rounded-lg border border-border-primary bg-bg-primary p-4">
      <div>
        <h2 className="text-sm font-semibold text-text-primary">{title}</h2>
        {description && <p className="mt-1 text-xs text-text-tertiary">{description}</p>}
      </div>
      {children}
    </section>
  );
}

type ImportType = "leads" | "sales";

const LEADS_MAPPING_FIELDS: [keyof LeadsMapping, string][] = [
  ["email", "E-Mail (Pflicht)"],
  // TASK-155 — freiwillig, aber sobald zugeordnet je Zeile Pflicht.
  ["event_date", "Event-Datum (freiwillig)"],
  ["phone", "Telefon"],
  ["first_name", "Vorname"],
  ["last_name", "Nachname"],
];

const SALES_MAPPING_FIELDS: [keyof SalesMapping, string][] = [
  ["email", "E-Mail (Pflicht)"],
  ["value", "Wert (Pflicht)"],
  ["event_date", "Event-Datum (Pflicht)"],
  ["currency", "Währung (Default EUR)"],
  ["order_id", "Bestell-Nr."],
  ["cash_in", "Geflossener Betrag (optional)"],
  ["phone", "Telefon"],
  ["first_name", "Vorname"],
  ["last_name", "Nachname"],
];

const REASON_LABEL: Record<string, string> = {
  invalid_email: "ungültige E-Mail",
  missing_email: "keine E-Mail",
  missing_value: "kein Wert",
  invalid_value: "ungültiger Wert",
  missing_event_date: "kein Event-Datum",
  invalid_event_date: "ungültiges Event-Datum",
};

// TASK-17: shared by both the preview error path and commit's onError below —
// the 20 MB size guard (apps/api/src/routes/tracking-admin.ts) can reject
// either request with the same "csv_too_large" code.
function importErrorMessage(e: unknown): string {
  const err = ((e as ApiError)?.body as { error?: string } | undefined)?.error;
  if (err === "csv_too_large") return "Datei zu groß — max. 20 MB.";
  if (err === "legal_basis_required") return "Rechtsgrundlage bestätigen, bevor committet werden kann.";
  if (err === "email_mapping_required" || err === "mapping_required") {
    return "Pflichtfelder zuordnen, bevor committet werden kann.";
  }
  return `Import fehlgeschlagen: ${(e as Error).message}`;
}

/**
 * Variante B (Split + Live-Vorschau, siehe docs/discovery/bestandskontakte-import/epic.md).
 * Import-Art links wählbar: "Leads" (TASK-10) oder "Sales" (historische Sales
 * mit echtem Event-Datum + optionalem Meta-Offline-Send, TASK-11) — beide
 * teilen denselben Zweiphasen-Flow (Vorschau -> Commit), nur die Zielfelder
 * unterscheiden sich.
 */
export function ProjectImportTab() {
  const projectId = useProjectId();
  const [importType, setImportType] = useState<ImportType>("leads");
  const [fileName, setFileName] = useState<string | null>(null);
  const [csv, setCsv] = useState<string | null>(null);
  const [leadsMapping, setLeadsMapping] = useState<LeadsMapping>(EMPTY_LEADS_MAPPING);
  const [salesMapping, setSalesMapping] = useState<SalesMapping>(EMPTY_SALES_MAPPING);
  // Getrennt wie leadsMapping/salesMapping — sonst würde ein frei getippter
  // Sales-Event-Typ beim Umschalten auf Leads mit rüberwandern.
  const [leadsEventType, setLeadsEventType] = useState("");
  const [salesEventType, setSalesEventType] = useState("");
  const [sendToMeta, setSendToMeta] = useState(false);
  const [legalBasisConfirmed, setLegalBasisConfirmed] = useState(false);
  const [committed, setCommitted] = useState<CommitLeadsResponse | CommitSalesResponse | null>(null);
  // Fehlerkachel-Filter (AC): welcher Status gerade auf die Stichprobe
  // gefiltert ist — null = kein Filter. Typ divergiert je Import-Art
  // (LeadRowStatus vs. SalesRowStatus), daher als string geführt und an den
  // beiden Preview-Aufrufen auf den passenden Typ gecastet.
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);

  const leadsPreview = useLeadsPreview(projectId);
  const leadsCommit = useCommitLeadsImport(projectId);
  const salesPreview = useSalesPreview(projectId);
  const salesCommit = useCommitSalesImport(projectId);
  const eventTypes = useEventTypes(projectId);

  const isSales = importType === "sales";
  // Shared skeleton fields (headers/delimiter/mappingValid/total/isPending)
  // have the identical shape on both result types — safe to read through a
  // merged reference. Counts/sample diverge (existing vs already_imported,
  // Sales' extra "Wert" column) and are read from the concrete
  // leadsPreview/salesPreview below instead, so no union-typed access there.
  const preview = isSales ? salesPreview : leadsPreview;
  const commit = isSales ? salesCommit : leadsCommit;

  const history = useImportHistory(projectId);
  const undoImport = useUndoImport(projectId);

  // Vor Commit verwerfen (AC): der Stapel lebt bis hierhin nur lokal im
  // Browser-State — nichts wurde geschrieben, also genügt ein reiner
  // State-Reset (kein Backend-Call nötig).
  const discardStaged = () => {
    setFileName(null);
    setCsv(null);
    setLeadsMapping(EMPTY_LEADS_MAPPING);
    setSalesMapping(EMPTY_SALES_MAPPING);
    setLeadsEventType("");
    setSalesEventType("");
    setSendToMeta(false);
    setLegalBasisConfirmed(false);
    setStatusFilter(null);
    setCommitted(null);
  };

  const onFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result ?? "");
      setCsv(text);
      setFileName(file.name);
      setLeadsMapping(EMPTY_LEADS_MAPPING);
      setSalesMapping(EMPTY_SALES_MAPPING);
      setStatusFilter(null);
      setCommitted(null);
      if (isSales) salesPreview.mutate({ csv: text, mapping: EMPTY_SALES_MAPPING });
      else leadsPreview.mutate({ csv: text, mapping: EMPTY_LEADS_MAPPING });
    };
    reader.readAsText(file, "utf-8");
  };

  // Live preview: re-runs whenever the mapping, the Fehlerkachel-Filter, or
  // the Import-Art itself changes, once a file is loaded.
  useEffect(() => {
    if (!csv) return;
    if (isSales) {
      salesPreview.mutate({
        csv,
        mapping: salesMapping,
        eventType: salesEventType.trim() || undefined,
        statusFilter: (statusFilter as SalesRowStatus) || undefined,
      });
    } else {
      leadsPreview.mutate({
        csv,
        mapping: leadsMapping,
        statusFilter: (statusFilter as LeadRowStatus) || undefined,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    csv,
    importType,
    leadsMapping.email,
    leadsMapping.phone,
    leadsMapping.first_name,
    leadsMapping.last_name,
    // TASK-155 — must be here for the same reason salesMapping.event_date is:
    // picking the date column changes what the preview counts (duplicate rule,
    // error rows). Without it the shown preview stays on the pre-mapping state
    // until some other field happens to move.
    leadsMapping.event_date,
    salesMapping.email,
    salesMapping.value,
    salesMapping.event_date,
    salesMapping.currency,
    salesMapping.order_id,
    salesMapping.cash_in,
    salesEventType,
    statusFilter,
  ]);

  const headers = preview.data?.headers ?? [];
  const salesMappingComplete = !!salesMapping.email && !!salesMapping.value && !!salesMapping.event_date;
  const canCommit =
    !!csv &&
    legalBasisConfirmed &&
    !commit.isPending &&
    (isSales ? salesMappingComplete : !!leadsMapping.email);

  // Erneuter Klick auf dieselbe Kachel hebt den Filter wieder auf (AC).
  const toggleFilter = (status: string) =>
    setStatusFilter((cur) => (cur === status ? null : status));

  const doCommit = () => {
    if (!csv) return;
    if (isSales) {
      salesCommit.mutate(
        {
          csv,
          mapping: salesMapping,
          legalBasisConfirmed,
          eventType: salesEventType.trim() || undefined,
          sendToMeta,
        },
        {
          onSuccess: (r) => {
            setCommitted(r);
            toast.success(`Import committed — ${r.imported} neu importiert`);
          },
          onError: (e) => toast.error(importErrorMessage(e)),
        },
      );
    } else {
      leadsCommit.mutate(
        {
          csv,
          mapping: leadsMapping,
          legalBasisConfirmed,
          eventType: leadsEventType.trim() || undefined,
          sendToMeta,
        },
        {
          onSuccess: (r) => {
            setCommitted(r);
            toast.success(`Import committed — ${r.imported} neu importiert`);
          },
          onError: (e) => toast.error(importErrorMessage(e)),
        },
      );
    }
  };

  return (
    <div className="grid gap-6 pb-12 lg:grid-cols-[320px_minmax(0,1fr)]">
      {/* Left: Datei + Import-Art */}
      <div className="space-y-4">
        <Box title="Datei" description="CSV mit Bestandsdaten — UTF-8, Komma oder Semikolon getrennt.">
          <label
            className={`flex cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed px-3 py-6 text-sm hover:bg-bg-hover ${
              dragActive
                ? "border-accent bg-accent/10 text-accent"
                : "border-border-primary bg-bg-secondary text-text-secondary"
            }`}
            onDragOver={(e) => {
              e.preventDefault();
              setDragActive(true);
            }}
            onDragLeave={() => setDragActive(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragActive(false);
              const f = e.dataTransfer.files?.[0];
              if (f) onFile(f);
            }}
          >
            <Upload size={16} />
            {fileName ?? "CSV-Datei wählen oder hierher ziehen…"}
            <input
              type="file"
              accept=".csv,text/csv"
              className="sr-only"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) onFile(f);
              }}
            />
          </label>
          {fileName && preview.data && (
            <p className="mt-2 text-xs text-text-tertiary">
              {preview.data.delimiter === ";" ? "Semikolon" : "Komma"}-getrennt erkannt
            </p>
          )}
          {csv && !committed && (
            <button
              type="button"
              onClick={discardStaged}
              className="mt-2 flex items-center gap-1.5 text-xs text-text-tertiary hover:text-danger"
            >
              <XCircle size={13} /> Verwerfen — es wurde noch nichts geschrieben
            </button>
          )}
        </Box>

        <Box title="Import-Art">
          <div className="flex gap-2">
            {(["leads", "sales"] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => {
                  setImportType(t);
                  setStatusFilter(null); // Status-Namen unterscheiden sich je Art (existing vs. already_imported)
                }}
                className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium ${
                  importType === t
                    ? "border-accent bg-accent/10 text-accent"
                    : "border-border-primary text-text-secondary hover:bg-bg-hover"
                }`}
              >
                {t === "leads" ? "Leads" : "Sales"}
              </button>
            ))}
          </div>
          <p className="mt-2 text-xs text-text-tertiary">
            {isSales
              ? "Historische Sales mit echtem Event-Datum — Event-Typ default Purchase."
              : "Bestandskontakte — mit zugeordnetem Event-Datum auf das echte Datum, ohne es auf den Import-Zeitpunkt."}
          </p>
        </Box>

        <Box
          title="Event-Typ"
          description={
            isSales
              ? "Default Purchase — vorhandene Typen wählen oder frei eintippen."
              : "Default Lead — vorhandene Typen wählen oder frei eintippen."
          }
        >
          <Combobox
            value={isSales ? salesEventType : leadsEventType}
            onChange={isSales ? setSalesEventType : setLeadsEventType}
            options={(eventTypes.data?.items ?? []).map((i) => ({ value: i.eventType, label: i.eventType }))}
            placeholder={isSales ? "Purchase" : "Lead"}
            allowCustom
          />
        </Box>
      </div>

      {/* Right: Mapping + Live-Vorschau + Commit */}
      <div className="space-y-4">
        <Box title="Spalten zuordnen" description="Nicht zugeordnete Spalten werden ignoriert.">
          {headers.length === 0 ? (
            <p className="text-sm text-text-secondary">Zuerst links eine CSV-Datei wählen.</p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              {(isSales ? SALES_MAPPING_FIELDS : LEADS_MAPPING_FIELDS).map(([field, label]) => (
                <div key={field}>
                  <label className="mb-1 block text-xs text-text-tertiary">{label}</label>
                  <select
                    className={inputCls}
                    value={(isSales ? salesMapping[field as keyof SalesMapping] : leadsMapping[field as keyof LeadsMapping]) ?? ""}
                    onChange={(e) => {
                      const v = e.target.value || null;
                      if (isSales) setSalesMapping((m) => ({ ...m, [field]: v }));
                      else setLeadsMapping((m) => ({ ...m, [field]: v }));
                    }}
                  >
                    <option value="">— nicht zuordnen —</option>
                    {headers.map((h) => (
                      <option key={h} value={h}>
                        {h}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          )}
        </Box>

        <Box title="Vorschau" description="Aktualisiert sich automatisch mit der Zuordnung — es wird noch nichts geschrieben.">
          {!csv ? (
            <p className="text-sm text-text-secondary">—</p>
          ) : isSales && !salesMappingComplete ? (
            <p className="flex items-center gap-2 text-sm text-warning">
              <AlertTriangle size={15} /> E-Mail, Wert und Event-Datum zuordnen, um eine Vorschau zu sehen.
            </p>
          ) : !isSales && !leadsMapping.email ? (
            <p className="flex items-center gap-2 text-sm text-warning">
              <AlertTriangle size={15} /> E-Mail-Spalte zuordnen, um eine Vorschau zu sehen.
            </p>
          ) : preview.isPending ? (
            <p className="text-sm text-text-secondary">Berechne…</p>
          ) : preview.isError ? (
            <p className="flex items-center gap-2 text-sm text-danger">
              <XCircle size={15} /> {importErrorMessage(preview.error)}
            </p>
          ) : preview.data?.mappingValid ? (
            <div className="space-y-3">
              <div className="grid grid-cols-4 gap-3">
                {(() => {
                  const vorhandenStatus = isSales ? "already_imported" : "existing";
                  const tileCls = (status: string) =>
                    `rounded-md border p-3 text-center transition-colors ${
                      statusFilter === status
                        ? "border-accent bg-accent/10"
                        : "border-border-primary bg-bg-secondary hover:bg-bg-hover"
                    }`;
                  return (
                    <>
                      <button type="button" onClick={() => toggleFilter("new")} className={tileCls("new")}>
                        <p className="text-2xl font-semibold text-success">{preview.data!.counts.new}</p>
                        <p className="text-xs text-text-tertiary">neu</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleFilter(vorhandenStatus)}
                        className={tileCls(vorhandenStatus)}
                      >
                        <p className="text-2xl font-semibold text-text-primary">
                          {isSales ? salesPreview.data?.counts.already_imported : leadsPreview.data?.counts.existing}
                        </p>
                        <p className="text-xs text-text-tertiary">vorhanden</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleFilter("duplicate")}
                        className={tileCls("duplicate")}
                      >
                        <p className="text-2xl font-semibold text-text-primary">
                          {preview.data!.counts.duplicates}
                        </p>
                        <p className="text-xs text-text-tertiary">Dublette</p>
                      </button>
                      <button type="button" onClick={() => toggleFilter("error")} className={tileCls("error")}>
                        <p className="text-2xl font-semibold text-danger">{preview.data!.counts.errors}</p>
                        <p className="text-xs text-text-tertiary">Fehler</p>
                      </button>
                    </>
                  );
                })()}
              </div>

              {preview.data.sample.length > 0 && (
                <div className="max-h-72 overflow-auto rounded-md border border-border-primary">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-bg-secondary">
                      <tr className="text-left text-text-tertiary">
                        <th className="px-2 py-1.5">Zeile</th>
                        <th className="px-2 py-1.5">E-Mail</th>
                        {isSales && <th className="px-2 py-1.5">Wert</th>}
                        <th className="px-2 py-1.5">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {isSales
                        ? salesPreview.data?.sample.map((r) => (
                            <tr key={r.row} className="border-t border-border-secondary">
                              <td className="px-2 py-1.5 text-text-tertiary">{r.row}</td>
                              <td className="px-2 py-1.5 font-mono text-text-primary">{r.email ?? "—"}</td>
                              <td className="px-2 py-1.5 text-text-primary">{r.value ?? "—"}</td>
                              <td
                                className={`px-2 py-1.5 ${
                                  r.status === "error" ? "text-danger" : r.status === "new" ? "text-success" : "text-text-secondary"
                                }`}
                              >
                                {r.status === "new"
                                  ? "neu"
                                  : r.status === "already_imported"
                                    ? "vorhanden"
                                    : r.status === "duplicate"
                                      ? "Dublette"
                                      : `Fehler (${REASON_LABEL[r.reason ?? ""] ?? r.reason})`}
                              </td>
                            </tr>
                          ))
                        : leadsPreview.data?.sample.map((r) => (
                            <tr key={r.row} className="border-t border-border-secondary">
                              <td className="px-2 py-1.5 text-text-tertiary">{r.row}</td>
                              <td className="px-2 py-1.5 font-mono text-text-primary">{r.email ?? "—"}</td>
                              <td
                                className={`px-2 py-1.5 ${
                                  r.status === "error" ? "text-danger" : r.status === "new" ? "text-success" : "text-text-secondary"
                                }`}
                              >
                                {r.status === "new"
                                  ? "neu"
                                  : r.status === "existing"
                                    ? "vorhanden"
                                    : r.status === "duplicate"
                                      ? "Dublette"
                                      : `Fehler (${REASON_LABEL[r.reason ?? ""] ?? r.reason})`}
                              </td>
                            </tr>
                          ))}
                    </tbody>
                  </table>
                  {preview.data.total > preview.data.sample.length && (
                    <p className="border-t border-border-secondary px-2 py-1.5 text-[11px] text-text-tertiary">
                      {statusFilter
                        ? `Gefiltert nach Status — ${preview.data.sample.length} Zeile(n) angezeigt (von ${preview.data.total} insgesamt).`
                        : `Stichprobe: ${preview.data.sample.length} von ${preview.data.total} Zeilen`}
                    </p>
                  )}
                </div>
              )}
            </div>
          ) : preview.data ? (
            // mappingValid: false with the required columns picked above means a
            // mapped column name isn't in this file — e.g. TASK-155's date
            // column after switching files. Saying nothing here leaves an empty
            // box and no way forward.
            <p className="flex items-center gap-2 text-sm text-warning">
              <AlertTriangle size={15} /> Eine zugeordnete Spalte gibt es in dieser Datei nicht — Zuordnung
              prüfen.
            </p>
          ) : null}
        </Box>

        <Box title="Rechtsgrundlage & Commit">
          <label className="flex items-start gap-2 text-sm text-text-primary">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={legalBasisConfirmed}
              onChange={(e) => setLegalBasisConfirmed(e.target.checked)}
            />
            <span>
              Für diesen Import liegt eine DSGVO-Rechtsgrundlage vor. Die Bestätigung wird mit
              Name und Zeitpunkt im Audit-Log protokolliert.
            </span>
          </label>

          <div className="mt-3">
            <label className="flex items-start gap-2 text-sm text-text-primary">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={sendToMeta}
                onChange={(e) => setSendToMeta(e.target.checked)}
              />
              <span>
                Auch an Meta senden (Default aus). Bei aktivem Haken geht je{" "}
                {isSales ? "Sale" : "importiertem Kontakt"} eine Offline-Conversion (echtes Datum)
                an Meta.
              </span>
            </label>
            <p className="mt-2 flex items-start gap-1.5 rounded-md border border-danger bg-danger-bg px-2.5 py-2 text-xs text-danger">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <span>
                Achtung: Der Versand an Meta ist nicht rückgängig zu machen. Das Import-Rückgängig
                entfernt nur die Daten bei uns — bereits an Meta gesendete Events bleiben dort
                bestehen.
              </span>
            </p>
            <p className="mt-1.5 text-xs text-text-tertiary">
              Hinweis: Der gewählte Event-Typ muss im Projekt unter „Meta" (Event-Routing)
              aktiviert sein — sonst wird trotz Haken nichts an Meta gesendet.
            </p>
          </div>

          <button
            type="button"
            disabled={!canCommit}
            onClick={doCommit}
            className="mt-3 rounded-md border border-accent bg-accent px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            {commit.isPending ? "Importiere…" : "Import committen"}
          </button>

          {committed && (
            <p className="mt-3 flex items-start gap-2 text-sm text-success">
              <CheckCircle2 size={15} className="mt-0.5 shrink-0" />
              Batch #{committed.batch_id}: {committed.imported} importiert,{" "}
              {committed.skipped_duplicates} bereits verarbeitet, {committed.errors} übersprungen
              (Fehler).
            </p>
          )}
        </Box>

        <Box
          title="Import-Historie"
          description={
            'Frühere Importe dieses Projekts. „Rückgängig" entfernt den kompletten Stapel (Kontakte, ' +
            "Events, Dedup-Marker) wieder — sofort im Projekt."
          }
        >
          {history.isPending ? (
            <p className="text-sm text-text-secondary">Lade…</p>
          ) : !history.data?.batches.length ? (
            <p className="text-sm text-text-secondary">Noch kein Import committet.</p>
          ) : (
            <ul className="space-y-2">
              {history.data.batches.map((b) => (
                <li
                  key={b.id}
                  className="flex items-center justify-between gap-3 rounded-md border border-border-primary bg-bg-secondary px-3 py-2 text-sm"
                >
                  <div>
                    <p className="font-medium text-text-primary">{b.label}</p>
                    <p className="text-xs text-text-tertiary">
                      {new Date(b.created_at).toLocaleString("de-DE")} · {b.counts.imported ?? "–"} importiert
                    </p>
                  </div>
                  <button
                    type="button"
                    disabled={undoImport.isPending}
                    onClick={() => {
                      if (
                        !window.confirm(
                          `Import „${b.label}" wirklich rückgängig machen? Alle Kontakte/Events dieses Stapels werden entfernt.`,
                        )
                      ) {
                        return;
                      }
                      undoImport.mutate(b.id, {
                        onSuccess: (r) => {
                          toast.success(
                            r.already_undone
                              ? `„${b.label}" war bereits rückgängig gemacht — nichts mehr zu entfernen.`
                              : `Rückgängig gemacht — ${r.deleted.events} Events, ${r.deleted.visitors} Kontakte entfernt. ${r.bigquery.note}`,
                          );
                        },
                        onError: (e) => toast.error(`Rückgängig fehlgeschlagen: ${(e as Error).message}`),
                      });
                    }}
                    className="flex shrink-0 items-center gap-1.5 rounded-md border border-border-primary px-2 py-1 text-xs text-text-secondary hover:border-danger hover:text-danger disabled:opacity-50"
                  >
                    <RotateCcw size={13} /> Rückgängig
                  </button>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-3 flex items-start gap-1.5 text-xs text-text-tertiary">
            <AlertTriangle size={13} className="mt-0.5 shrink-0" />
            Importe werden gespiegelt, wenn BigQuery-Sync für dieses Projekt aktiv ist. „Rückgängig" entfernt die
            gespiegelten Zeilen dann asynchron im Hintergrund — frisch gestreamte Zeilen lassen sich erst nach dem
            ~90-Minuten-Streaming-Buffer entfernen.
          </p>
        </Box>
      </div>
    </div>
  );
}
